import React, { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { WindowLoadingFallback } from "@/components/ui/os-layout/WindowLoadingFallback";
import {
  CHUNK_RELOAD_SESSION_KEY,
  isChunkLoadError,
  reloadOnceForStaleChunks,
} from "@/utils/chunkLoadError";
import {
  logAniOsChunkLoadError,
  logAniOsWindowRenderError,
} from "@/utils/aniosDiagnostics";
import { ChunkLoadErrorFallback } from "./ChunkLoadErrorFallback";

interface ChunkLoadAwareErrorBoundaryProps {
  children: ReactNode;
  variant?: "page" | "window";
}

interface ChunkLoadAwareErrorBoundaryState {
  error: Error | null;
  reloading: boolean;
}

/**
 * Catches render errors from lazy-loaded children.
 * Page variant: one-shot full reload for stale deploy chunks.
 * Window variant: in-window fallback only (no full aniOS bootstrap reload).
 */
export class ChunkLoadAwareErrorBoundary extends Component<
  ChunkLoadAwareErrorBoundaryProps,
  ChunkLoadAwareErrorBoundaryState
> {
  state: ChunkLoadAwareErrorBoundaryState = {
    error: null,
    reloading: false,
  };

  static getDerivedStateFromError(
    error: Error,
  ): Partial<ChunkLoadAwareErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const variant = this.props.variant ?? "window";

    if (isChunkLoadError(error)) {
      logAniOsChunkLoadError("error-boundary", error, {
        variant,
        componentStack: errorInfo.componentStack,
      });

      if (variant === "window") {
        return;
      }

      if (reloadOnceForStaleChunks({ source: "page-error-boundary" })) {
        this.setState({ reloading: true, error: null });
      }

      Sentry.withScope((scope) => {
        scope.setTag("chunk_load", "true");
        scope.setContext("react", { componentStack: errorInfo.componentStack });
        Sentry.captureException(error);
      });
      return;
    }

    logAniOsWindowRenderError(error, {
      variant,
      componentStack: errorInfo.componentStack,
    });

    Sentry.withScope((scope) => {
      scope.setTag("chunk_load", "false");
      scope.setContext("react", { componentStack: errorInfo.componentStack });
      Sentry.captureException(error);
    });
  }

  render(): ReactNode {
    if (this.state.reloading) {
      return <WindowLoadingFallback />;
    }

    if (this.state.error) {
      const variant = this.props.variant ?? "window";

      if (isChunkLoadError(this.state.error)) {
        if (variant === "window") {
          return (
            <ChunkLoadErrorFallback
              variant="window"
              data-testid="chunk-load-error-boundary-fallback"
            />
          );
        }

        let reloadAlreadyAttempted = false;
        try {
          reloadAlreadyAttempted =
            typeof sessionStorage !== "undefined" &&
            sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === "1";
        } catch {
          reloadAlreadyAttempted = false;
        }

        if (!reloadAlreadyAttempted || this.state.reloading) {
          return <WindowLoadingFallback />;
        }

        return (
          <ChunkLoadErrorFallback
            variant={variant}
            data-testid="chunk-load-error-boundary-fallback"
          />
        );
      }

      return (
        <div
          className="flex flex-col items-center justify-center h-full min-h-[200px] p-8 text-center"
          data-testid="window-error-fallback"
        >
          <h2 className="text-lg font-semibold text-slate-200">
            This window failed to load
          </h2>
          <p className="mt-2 text-sm text-slate-400 max-w-md">
            Try closing and reopening the window, or refresh the app.
          </p>
          {import.meta.env.DEV && this.state.error ? (
            <pre
              className="mt-4 max-h-40 w-full max-w-lg overflow-auto rounded-lg bg-black/40 p-3 text-left text-xs text-rose-300"
              data-testid="window-error-details"
            >
              {this.state.error.message}
            </pre>
          ) : null}
        </div>
      );
    }

    return this.props.children;
  }
}
